import { useEffect, useRef, useState } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Spinner } from 'common/Spinner/Spinner'
import { Microphone, Pause, Play3, Stop } from 'blaze-ui/icons/20'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { ensureUploadFile } from 'utils/ensureUploadFilename'
import { MAX_RECORDING_MS, MIN_RECORDING_MS, RecorderErrorKind, useVoiceRecorder } from './useVoiceRecorder'
import Styles from './VoiceRecorder.module.scss'

const ERROR_COPY: Record<RecorderErrorKind, string> = {
  denied: 'Microphone access was blocked. Allow it in your browser, or use Upload sample instead.',
  'no-device': 'No microphone was found. Connect one, or use Upload sample instead.',
  'in-use': 'Your microphone is in use by another app. Close it and try again.',
  unsupported: 'Recording isn’t supported in this browser. Use Upload sample instead.',
  unknown: 'Something went wrong while recording. Try again, or use Upload sample instead.',
}

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  return `${minutes}:${seconds}`
}

interface Props {
  uploadsProjectId?: string
  initialMediaFileId?: number
  initialSampleUrl?: string
  onChange: (mediaFileId: number | null) => void
}

export const VoiceRecorder = ({ uploadsProjectId, initialMediaFileId, initialSampleUrl, onChange }: Props) => {
  const { state, durationMs, errorKind, recording, isSupported, start, stop, reset } = useVoiceRecorder()
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(initialSampleUrl ?? '')
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [hasInitialSample, setHasInitialSample] = useState(!!initialMediaFileId)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const uploadedRef = useRef(false)

  useEffect(() => {
    if (state !== 'recorded' || !recording || uploadedRef.current) return
    uploadedRef.current = true
    const uploadRecording = async () => {
      setIsUploading(true)
      try {
        const file = ensureUploadFile(new File([recording.blob], 'voice-recording', { type: recording.mimeType }))
        const uploaded = await uploadImageFilesWithoutLoadingToast([file], uploadsProjectId)
        const mediaFile = uploaded?.[0]
        if (mediaFile?.media_file_id) {
          setPreviewUrl(mediaFile.source_url ?? '')
          onChange(mediaFile.media_file_id)
        }
      } finally {
        setIsUploading(false)
      }
    }
    uploadRecording()
  }, [state, recording, uploadsProjectId, uploadImageFilesWithoutLoadingToast, onChange])

  useEffect(() => () => previewAudioRef.current?.pause(), [])

  const handleReRecord = () => {
    previewAudioRef.current?.pause()
    previewAudioRef.current = null
    setIsPreviewing(false)
    setPreviewUrl('')
    setHasInitialSample(false)
    uploadedRef.current = false
    onChange(null)
    reset()
  }

  const handlePreview = () => {
    if (!previewUrl) return
    if (isPreviewing) {
      previewAudioRef.current?.pause()
      setIsPreviewing(false)
      return
    }
    const audio = new Audio(previewUrl)
    previewAudioRef.current = audio
    audio.onended = () => setIsPreviewing(false)
    audio.play().catch(() => setIsPreviewing(false))
    setIsPreviewing(true)
  }

  if (!isSupported) {
    return (
      <div className={Styles.recorder}>
        <Text variant="secondary">{ERROR_COPY.unsupported}</Text>
      </div>
    )
  }

  if (state === 'error' && errorKind) {
    return (
      <div className={Styles.recorder}>
        <Text variant="smallList" color="var(--red-70)">
          {ERROR_COPY[errorKind]}
        </Text>
        <button type="button" className={Styles.linkButton} onClick={handleReRecord}>
          <Text variant="secondary">Try again</Text>
        </button>
      </div>
    )
  }

  if (isUploading) {
    return (
      <div className={Styles.recorder}>
        <div className={Styles.uploading} data-testid="voice-recording-uploading">
          <Spinner variant="dark" />
          <Text variant="secondary">Processing…</Text>
        </div>
      </div>
    )
  }

  if (state === 'recorded' || hasInitialSample) {
    return (
      <div className={Styles.recorder}>
        <div className={Styles.recordedRow}>
          <IconButton
            icon={isPreviewing ? Pause : Play3}
            size="sm"
            variant="tertiary"
            isDisabled={!previewUrl}
            onPress={handlePreview}
            aria-label="Preview recording"
          />
          <Text variant="smallList">Voice recording</Text>
          <button type="button" className={Styles.linkButton} onClick={handleReRecord}>
            <Text variant="secondary">Re-record</Text>
          </button>
        </div>
      </div>
    )
  }

  if (state === 'recording') {
    const progress = Math.min(1, durationMs / MAX_RECORDING_MS)
    const canStop = durationMs >= MIN_RECORDING_MS
    return (
      <div className={Styles.recorder}>
        <div className={Styles.recordingRow}>
          <IconButton
            icon={Stop}
            size="sm"
            variant="primary"
            isDisabled={!canStop}
            onPress={stop}
            aria-label="Stop recording"
          />
          <div className={Styles.meter} aria-hidden="true">
            <span
              className={Styles.meterFill}
              data-testid="voice-recording-meter-fill"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <Text variant="smallList">
            {formatDuration(durationMs)} / {formatDuration(MAX_RECORDING_MS)}
          </Text>
        </div>
        {!canStop && <Text variant="secondary">Record at least 4 seconds</Text>}
      </div>
    )
  }

  if (state === 'requesting') {
    return (
      <div className={Styles.recorder}>
        <Text variant="secondary">Allow microphone access…</Text>
      </div>
    )
  }

  return (
    <button type="button" className={Styles.startButton} onClick={start} aria-label="Start recording">
      <span className={Styles.startIcon}>
        <Microphone size={20} />
      </span>
      <Text fontWeight="500">Start recording</Text>
      <Text variant="secondary">~10–20s of natural speech works best</Text>
    </button>
  )
}
